import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useContext, useEffect, useState } from 'react';
import {
  GovernanceTagsContextType,
  GovernanceTagsContext,
  FlatResourcesQueryParams,
  PaginatedResourcesResponse,
  ResourceData,
  ResourceFilter,
} from '../types';
import GovernanceTagsApi from '../api/governanceTagsApi';
import toast from 'shared/components/andtComponents/Toast';
import { useGovernanceTagsProcessing } from './useGovernanceTagsProcessing';

export const FIVE_MINUTES = 5 * 60 * 1000;
export const TEN_MINUTES = 10 * 60 * 1000;

const QUERY_KEYS = {
  GOVERNANCE_TAGS: ['governance', 'tags'],
  TAGGED_RESOURCES: ['governance', 'taggedResources'],
  UNTAGGED_RESOURCES: ['governance', 'untaggedResources'],
  ALL_RESOURCES: ['governance', 'allResources'],
  GOVERNANCE_METRICS: ['governance', 'metrics'],
  RESOURCES: ['governance', 'resources'],
  PAGINATED_RESOURCES: ['governance', 'paginatedResources'],
  DISTINCT_TAG_VALUES: ['governance', 'distinctTagValues'],
} as const;

const extractResourceIds = (params: { resourceIds?: string | string[]; resourceId?: string }): string[] => {
  if (Array.isArray(params.resourceIds)) {
    return params.resourceIds.filter((id): id is string => Boolean(id));
  }
  if (params.resourceId) {
    return [params.resourceId];
  }
  return [];
};

const createOptimisticUpdate = (
  queryClient: ReturnType<typeof useQueryClient>,
  resourceIds: string[],
  updateFn: (resource: ResourceData) => ResourceData,
) => {
  const previousQueries = queryClient.getQueriesData({ queryKey: [QUERY_KEYS.PAGINATED_RESOURCES] });

  previousQueries.forEach(([queryKey]) => {
    queryClient.setQueryData(queryKey, (oldData: PaginatedResourcesResponse | undefined) => {
      if (!oldData?.resources) return oldData;

      return {
        ...oldData,
        resources: oldData.resources.map((resource: ResourceData) =>
          resourceIds.includes(resource.resourceId) ? updateFn(resource) : resource,
        ),
      };
    });
  });

  return previousQueries;
};

const rollbackOptimisticUpdate = (
  queryClient: ReturnType<typeof useQueryClient>,
  previousQueries: [readonly unknown[], unknown][],
) => {
  previousQueries.forEach(([queryKey, data]) => {
    queryClient.setQueryData(queryKey, data);
  });
};

export const useGovernanceTags = () => {
  const context = useContext(GovernanceTagsContext) as GovernanceTagsContextType;
  const queryClient = useQueryClient();
  const api = new GovernanceTagsApi();
  const { addProcessingResource, removeProcessingResource } = useGovernanceTagsProcessing();

  const [resourceFilter, setResourceFilter] = useState<ResourceFilter>(ResourceFilter.ALL);

  if (context === undefined) {
    throw new Error('useGovernanceTags must be used within a GovernanceTagsProvider');
  }

  const { data: resources, isLoading: isLoadingResources } = useQuery({
    queryKey: [QUERY_KEYS.RESOURCES, resourceFilter, context.filterParams],
    queryFn: () => api.getResources(resourceFilter, context.filterParams),
    staleTime: 0,
    gcTime: 0,
  });

  const { data: metricsData, isLoading: isLoadingMetrics } = useQuery({
    queryKey: QUERY_KEYS.GOVERNANCE_METRICS,
    queryFn: () => api.getGovernanceMetrics(),
  });

  const { data: distinctTagValuesData, isLoading: isLoadingDistinctTagValues } = useQuery({
    queryKey: QUERY_KEYS.DISTINCT_TAG_VALUES,
    queryFn: () => api.getDistinctTagValues(),
    staleTime: FIVE_MINUTES,
    gcTime: TEN_MINUTES,
  });

  useEffect(() => {
    if (context && metricsData) {
      context.setMetrics(metricsData);
    }
  }, [metricsData, context]);

  useEffect(() => {
    if (context && distinctTagValuesData) {
      context.setDistinctTagValues(distinctTagValuesData);
    }
  }, [distinctTagValuesData, context]);

  const addGovernanceTagsMutation = useMutation({
    mutationFn: api.addGovernanceTags,
    onMutate: async (params) => {
      const resourceIds = extractResourceIds(params);
      resourceIds.forEach(addProcessingResource);
      await queryClient.cancelQueries({ queryKey: [QUERY_KEYS.PAGINATED_RESOURCES] });

      const previousQueries = createOptimisticUpdate(queryClient, resourceIds, (resource) => ({
        ...resource,
        governanceTags: [...(resource.governanceTags || []), ...params.governanceTags],
      }));

      return { previousQueries };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.RESOURCES] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.PAGINATED_RESOURCES] });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.GOVERNANCE_METRICS });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.DISTINCT_TAG_VALUES });
    },
    onError: (_, params, context) => {
      const resourceIds = extractResourceIds(params);
      resourceIds.forEach(removeProcessingResource);
      if (context?.previousQueries) {
        rollbackOptimisticUpdate(queryClient, context.previousQueries);
      }
      toast.error('Failed to add virtual tags');
    },
  });

  const updateGovernanceTagsMutation = useMutation({
    mutationFn: api.updateGovernanceTags,
    onMutate: async (params) => {
      const resourceIds = extractResourceIds(params);
      resourceIds.forEach(addProcessingResource);
      await queryClient.cancelQueries({ queryKey: [QUERY_KEYS.PAGINATED_RESOURCES] });

      const previousQueries = createOptimisticUpdate(queryClient, resourceIds, (resource) => ({
        ...resource,
        governanceTags: params.governanceTags,
      }));

      return { previousQueries };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.RESOURCES] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.PAGINATED_RESOURCES] });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.GOVERNANCE_METRICS });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.DISTINCT_TAG_VALUES });
    },
    onError: (_, params, context) => {
      const resourceIds = extractResourceIds(params);
      resourceIds.forEach(removeProcessingResource);
      if (context?.previousQueries) {
        rollbackOptimisticUpdate(queryClient, context.previousQueries);
      }
      toast.error('Failed to update virtual tags');
    },
  });

  const deleteGovernanceTagsMutation = useMutation({
    mutationFn: api.deleteGovernanceTags,
    onMutate: async (resourceIds) => {
      const ids = Array.isArray(resourceIds) ? resourceIds : [resourceIds];
      ids.forEach(addProcessingResource);
      await queryClient.cancelQueries({ queryKey: [QUERY_KEYS.PAGINATED_RESOURCES] });

      const previousQueries = createOptimisticUpdate(queryClient, ids, (resource) => ({
        ...resource,
        governanceTags: [],
      }));

      return { previousQueries };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.RESOURCES] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.PAGINATED_RESOURCES] });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.GOVERNANCE_METRICS });
    },
    onError: (_, resourceIds, context) => {
      const ids = Array.isArray(resourceIds) ? resourceIds : [resourceIds];
      ids.forEach(removeProcessingResource);
      if (context?.previousQueries) {
        rollbackOptimisticUpdate(queryClient, context.previousQueries);
      }
      toast.error('Failed to delete virtual tags');
    },
  });

  return {
    ...context,
    resources,
    isLoadingResources,
    isLoadingDistinctTagValues,
    isLoadingMetrics,
    resourceFilter,
    setResourceFilter,
    addGovernanceTags: addGovernanceTagsMutation.mutateAsync,
    updateGovernanceTags: updateGovernanceTagsMutation.mutateAsync,
    deleteGovernanceTags: deleteGovernanceTagsMutation.mutateAsync,
  };
};

export const useGovernanceTagsPaginated = (queryParams: FlatResourcesQueryParams) => {
  const queryClient = useQueryClient();
  const api = new GovernanceTagsApi();

  const paginatedResourcesQuery = useQuery({
    queryKey: [
      QUERY_KEYS.PAGINATED_RESOURCES,
      queryParams.filter,
      queryParams.service,
      queryParams.region,
      queryParams.resourceName,
      queryParams.resourceid,
      queryParams.linkedaccid,
      queryParams.tags,
      queryParams.governancetags,
      queryParams.page,
      queryParams.pageSize,
      queryParams.searchText,
      queryParams.sortBy,
      JSON.stringify(queryParams),
    ],
    queryFn: () => api.getPaginatedResources(queryParams),
    staleTime: 0,
    gcTime: 0,
    enabled: !!queryParams.page && !!queryParams.pageSize,
  });

  const invalidatePaginatedResources = () => {
    queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.PAGINATED_RESOURCES] });
  };

  return {
    ...paginatedResourcesQuery,
    invalidatePaginatedResources,
  };
};

export const useGovernanceTagsForExport = (queryParams: Omit<FlatResourcesQueryParams, 'page' | 'pageSize'>) => {
  const api = new GovernanceTagsApi();

  return useQuery({
    queryKey: [
      'GOVERNANCE_TAGS_EXPORT',
      queryParams.filter,
      queryParams.service,
      queryParams.region,
      queryParams.resourceName,
      queryParams.resourceid,
      queryParams.linkedaccid,
      queryParams.tags,
      queryParams.governancetags,
      queryParams.searchText,
      queryParams.sortBy,
    ],
    queryFn: () => api.getAllResourcesForExport(queryParams),
    staleTime: 0,
    gcTime: 0,
    enabled: false,
  });
};
